/**
 * FacilitatorControls
 *
 * Elevated facilitator action panel for the Session Run tab.
 * Only rendered when the current user is the session facilitator.
 *
 * Surfaces the actions a facilitator actually needs in sequence:
 *   1. Open session for participants (set state → CRITERIA_FINALIZED, send invites)
 *   2. Monitor who has joined / submitted
 *   3. Close participation window
 *   4. Publish results (set state → PUBLISHED)
 *
 * These actions already exist deep in SessionDetail — this component
 * surfaces them at the top of the Run tab so the facilitator doesn't
 * have to hunt through tabs to do their job.
 *
 * Canon: §1D Goodness Principle
 */

import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Lock,
  Play,
  Send,
  UserCheck,
  Users,
} from 'lucide-react';
import type { PrioritizationSession } from '../../../types/prioritization/session';
// The session.ts `SessionParticipant` is an unrelated display-shape type
// (name/email/joinedAt) reused for a different concept. This component only
// reads `.length`, and its only caller passes the sessionParticipantsStore
// records — use that store's shape, which is what's actually on the wire.
import type { SessionParticipant } from '../../../stores/prioritization/sessionParticipantsStore';
import { LOCK_CRITERIA_LABEL, OPEN_FOR_PARTICIPANTS_LABEL } from '@/lib/sessions/vocabulary';

// ── Step states ───────────────────────────────────────────────────────────────

type FacilitatorStep =
  | 'lock_config'    // Session is DRAFT — needs to be locked before running
  | 'open_session'   // Config locked, ready to open for participants
  | 'collecting'     // Session open — waiting for participants
  | 'close_session'  // All or enough participants submitted — can close
  | 'publish'        // Run complete — publish results
  | 'done';          // Published

const STEP_META: Record<
  FacilitatorStep,
  { label: string; description: string; icon: typeof Play }
> = {
  lock_config: {
    label: LOCK_CRITERIA_LABEL,
    description: 'Review the items, criteria, and participants, then lock the criteria to make this session ready to run.',
    icon: Lock,
  },
  open_session: {
    label: OPEN_FOR_PARTICIPANTS_LABEL,
    description: 'Configuration is locked. Open the session so participants can join and begin their ranking.',
    icon: Play,
  },
  collecting: {
    label: 'Collecting responses',
    description: 'Waiting for participants to submit their input.',
    icon: Users,
  },
  close_session: {
    label: 'Close participation',
    description: "Enough participants have submitted. Close the window when you're ready to process results.",
    icon: Lock,
  },
  publish: {
    label: 'Publish results',
    description: 'All responses collected. Review and publish the final ranked output.',
    icon: Send,
  },
  done: {
    label: 'Results published',
    description: 'This session is complete. Results are available for review.',
    icon: CheckCircle2,
  },
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface FacilitatorControlsProps {
  session: PrioritizationSession;
  participants: SessionParticipant[];
  submittedCount: number;
  onLockConfig?: () => Promise<void>;
  onOpenSession?: () => Promise<void>;
  onCloseSession?: () => Promise<void>;
  onPublish?: () => Promise<void>;
  isProcessing?: boolean;
  /** Override the computed step — used when local UI state is ahead of session.state (e.g. live session open). */
  forceStep?: FacilitatorStep;
  /** Shown under a step's header while that step is current — e.g. the
   *  optional rationale beside Lock criteria (985). */
  stepExtras?: Partial<Record<FacilitatorStep, ReactNode>>;
}

// ── Participant progress strip ─────────────────────────────────────────────────

function ParticipantProgressStrip({
  total,
  submitted,
}: {
  total: number;
  submitted: number;
}) {
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
        <div
          className="h-full bg-[var(--slate)] rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-shrink-0 w-24 text-right">
        <UserCheck className="h-3 w-3 inline mr-1 text-[var(--text-ter)]" />
        {submitted} / {total} submitted
      </span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FacilitatorControls({
  session,
  participants,
  submittedCount,
  onLockConfig,
  onOpenSession,
  onCloseSession,
  onPublish,
  isProcessing = false,
  forceStep,
  stepExtras,
}: FacilitatorControlsProps) {
  const computedStep = useMemo((): FacilitatorStep => {
    switch (session.state) {
      case 'DRAFT':                return 'lock_config';
      case 'CRITERIA_FINALIZED':   return 'open_session';
      case 'RUN_COMPLETE':         return 'publish';
      case 'PUBLISHED':            return 'done';
      default: {
        const pct = participants.length > 0
          ? submittedCount / participants.length
          : 0;
        return pct >= 0.8 ? 'close_session' : 'collecting';
      }
    }
  }, [session.state, participants.length, submittedCount]);

  const step = forceStep ?? computedStep;

  const meta = STEP_META[step];
  const Icon = meta.icon;
  const isDone = step === 'done';

  const actionMap: Partial<Record<FacilitatorStep, () => Promise<void>>> = {
    lock_config:   onLockConfig,
    open_session:  onOpenSession,
    close_session: onCloseSession,
    publish:       onPublish,
  };

  const action = actionMap[step];

  return (
    <div
      className={` border px-6 py-5 space-y-4 ${
        isDone
          ? 'border-[var(--green)] dark:border-[var(--green)]/40 bg-[var(--green-dim)] dark:bg-[var(--green)]/10'
          : 'border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/10'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 w-8 h-8  flex items-center justify-center flex-shrink-0 ${
              isDone ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/30' : 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30'
            }`}
          >
            <Icon className={`h-4 w-4 ${isDone ? 'text-[var(--green)]' : 'text-[var(--text-ter)]'}`} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-0.5">
              Facilitator
            </p>
            <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {meta.label}
            </p>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
              {meta.description}
            </p>
          </div>
        </div>

        {action && !isDone && (
          <button
            disabled={isProcessing}
            onClick={() => void action()}
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white transition-colors disabled:opacity-40"
          >
            <Icon className="h-4 w-4" />
            {meta.label}
          </button>
        )}
      </div>

      {!isDone && stepExtras?.[step]}

      {/* Participant progress — shown while collecting */}
      {(step === 'collecting' || step === 'close_session' || step === 'publish') &&
        participants.length > 0 && (
          <ParticipantProgressStrip
            total={participants.length}
            submitted={submittedCount}
          />
        )}

      {/* Quick link to full session for admin detail */}
      {!isDone && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Need more control?{' '}
          <Link
            to={`/prioritization/sessions/${session.id}`}
            className="text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
          >
            Open full session
          </Link>
        </p>
      )}
    </div>
  );
}

export default FacilitatorControls;
